import { useEffect, useRef } from "react";
import type { DrawClient, Stroke } from "./types";

/** Reference size for the normalized coordinate space every device draws
 *  into — points are sent/received as fractions of this box, then scaled to
 *  each device's actual canvas size, so the drawing looks identical (same
 *  aspect, no skew) on every screen regardless of resolution. */
const VW = 480, VH = 360;

export const DRAW_COLORS = ["#14181f", "#ef4444", "#2563eb", "#15b67a", "#f59e0b", "#a855f7"];
export const BRUSH_SIZES = [3, 7, 14] as const;
const DEFAULT_WIDTH = 7;

export interface DrawCanvasApi {
  /** Remove the last stroke drawn locally + tell the server/relay. No-op if
   *  there's nothing to undo. */
  undo: () => void;
  /** Wipe the canvas locally + tell the server/relay. The server excludes
   *  the sender from its `clear` relay (same as the stroke relay), so
   *  without this the drawer's OWN screen never actually clears. */
  clear: () => void;
}

interface Props {
  client: DrawClient | null;
  /** True for the current drawer's own device — captures pointer input. */
  interactive: boolean;
  /** Existing strokes to replay on mount (late join / reconnect). */
  initialStrokes: Stroke[];
  /** Bumps to force a full replay + clear (new round). */
  resetKey: string | number;
  color: string;
  /** Brush width in the VW=480 reference space (see BRUSH_SIZES). */
  width?: number;
  className?: string;
  /** Filled with `{ undo }` once mounted, for the parent's Undo button. */
  apiRef?: React.MutableRefObject<DrawCanvasApi | null>;
}

export function DrawCanvas({ client, interactive, initialStrokes, resetKey, color, width = DEFAULT_WIDTH, className, apiRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const lastSendRef = useRef(0);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const colorRef = useRef(color);
  colorRef.current = color;
  const widthRef = useRef(width);
  widthRef.current = width;
  /** Mirrors what THIS device has drawn/replayed so Undo can pop + redraw
   *  without waiting on a server round-trip. */
  const localStrokesRef = useRef<Stroke[]>([]);

  // (Re)draw the full stroke history whenever the round resets.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctxRef.current = ctx;
    localStrokesRef.current = [...initialStrokes];
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      redraw(ctx, canvas, localStrokesRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Live stroke relay from other devices (drawer draws its own optimistically).
  useEffect(() => {
    if (!client) return;
    let curPath: { x: number; y: number } | null = null;
    const off = client.on((e) => {
      const canvas = canvasRef.current, ctx = ctxRef.current;
      if (!canvas || !ctx) return;
      if (e.type === "clear") { clearCanvas(ctx, canvas); localStrokesRef.current = []; curPath = null; return; }
      if (e.type !== "stroke-point") return;
      const px = e.x * canvas.width;
      const py = e.y * canvas.height;
      const w = ((e.width ?? DEFAULT_WIDTH) / VW) * canvas.width;
      if (e.newStroke || !curPath) {
        curPath = { x: px, y: py };
        dot(ctx, px, py, w, e.color ?? "#14181f");
        localStrokesRef.current.push({ points: [{ x: e.x, y: e.y }], color: e.color ?? "#14181f", width: e.width ?? DEFAULT_WIDTH });
      } else {
        line(ctx, curPath.x, curPath.y, px, py, w, e.color ?? "#14181f");
        curPath = { x: px, y: py };
        const last = localStrokesRef.current[localStrokesRef.current.length - 1];
        if (last) last.points.push({ x: e.x, y: e.y });
      }
    });
    return off;
  }, [client]);

  // Expose the Undo/Clear actions to the parent toolbar.
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      undo: () => {
        if (localStrokesRef.current.length === 0) return;
        localStrokesRef.current.pop();
        const canvas = canvasRef.current, ctx = ctxRef.current;
        if (canvas && ctx) redraw(ctx, canvas, localStrokesRef.current);
        client?.undoStroke();
      },
      clear: () => {
        localStrokesRef.current = [];
        const canvas = canvasRef.current, ctx = ctxRef.current;
        if (canvas && ctx) clearCanvas(ctx, canvas);
        client?.clearCanvas();
      },
    };
    return () => { if (apiRef) apiRef.current = null; };
  }, [client, apiRef]);

  const toNorm = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)), y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)) };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactive) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const { x, y } = toNorm(e.clientX, e.clientY);
    const canvas = canvasRef.current, ctx = ctxRef.current;
    const w = widthRef.current;
    if (canvas && ctx) dot(ctx, x * canvas.width, y * canvas.height, (w / VW) * canvas.width, colorRef.current);
    lastPtRef.current = canvas ? { x: x * canvas.width, y: y * canvas.height } : null;
    localStrokesRef.current.push({ points: [{ x, y }], color: colorRef.current, width: w });
    client?.strokePoint(x, y, true, colorRef.current, w);
    lastSendRef.current = performance.now();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactive || !drawingRef.current) return;
    const now = performance.now();
    if (now - lastSendRef.current < 28) return; // ~35/s cap — plenty smooth, light on the DO
    lastSendRef.current = now;
    const { x, y } = toNorm(e.clientX, e.clientY);
    const canvas = canvasRef.current, ctx = ctxRef.current;
    const w = widthRef.current;
    if (canvas && ctx) {
      const px = x * canvas.width, py = y * canvas.height;
      const pw = (w / VW) * canvas.width;
      if (lastPtRef.current) line(ctx, lastPtRef.current.x, lastPtRef.current.y, px, py, pw, colorRef.current);
      lastPtRef.current = { x: px, y: py };
    }
    const last = localStrokesRef.current[localStrokesRef.current.length - 1];
    if (last) last.points.push({ x, y });
    client?.strokePoint(x, y, false, colorRef.current, w);
  };
  const onPointerUp = () => { drawingRef.current = false; lastPtRef.current = null; };

  return (
    <canvas
      ref={canvasRef}
      className={`drawit-canvas ${className ?? ""} ${interactive ? "is-interactive" : ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{ touchAction: "none" }}
    />
  );
}

function clearCanvas(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}
function dot(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, w / 2, 0, Math.PI * 2);
  ctx.fill();
}
function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, w: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}
function redraw(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, strokes: Stroke[]) {
  clearCanvas(ctx, canvas);
  for (const s of strokes) {
    if (s.points.length === 0) continue;
    const w = (s.width / VW) * canvas.width;
    const p0 = s.points[0]!;
    dot(ctx, p0.x * canvas.width, p0.y * canvas.height, w, s.color);
    for (let i = 1; i < s.points.length; i++) {
      const a = s.points[i - 1]!, b = s.points[i]!;
      line(ctx, a.x * canvas.width, a.y * canvas.height, b.x * canvas.width, b.y * canvas.height, w, s.color);
    }
  }
}
